import { PhoneNumberTypes } from 'Constants/enums';
import { ContactModelV2 } from 'Models/ContactModelV2';
import type { TransferPhoneNumber, TransferTarget } from 'Modules/softphone/types';
import type { RootStoreProps } from 'Stores/RootStore.types';

export interface ResolvePhoneNumbersReturn {
  phoneNumbers: TransferPhoneNumber[];
  isLoaded: boolean;
}

type PersonStore = RootStoreProps['person'];

const OFFICE_LABEL = 'Office';
const PHONE_FALLBACK_LABEL = 'Phone';
const EXTENSION_LABEL = 'Extension';
const DIRECTORY_SOURCE: ContactModelV2['source'] = 'Bhive';

/**
 * Resolves the human-readable label for a phone number entry, matching the
 * legacy transfer list (Office / Mobile / Business / Home / ...).
 */
function getPhoneLabel(type: string | undefined): string {
  return PhoneNumberTypes.find((t) => t.key === type)?.text ?? PHONE_FALLBACK_LABEL;
}

/**
 * Normalizes a phone number for equality comparison.
 * Strips spaces, parens, dashes and the leading "+" so visually different
 * representations of the same number compare equal.
 */
function normalizePhoneNumber(value: string | undefined | null): string {
  return (value ?? '').replace(/[\s()\-+]/g, '');
}

/**
 * Filters a list of phone entries, deduplicating by normalized number against
 * an existing Set of keys (mutated in place to accumulate seen numbers).
 */
function deduplicatePhoneEntries<T extends { number?: string | null }>(
  entries: T[],
  seenKeys: Set<string>,
  additionalFilter?: (entry: T) => boolean
): T[] {
  return entries.filter((entry) => {
    if (!entry.number) {
      return false;
    }
    if (additionalFilter && !additionalFilter(entry)) {
      return false;
    }
    const key = normalizePhoneNumber(entry.number);
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return true;
  });
}

/**
 * Maps a ContactModelV2 to a list of TransferPhoneNumber entries.
 *
 * The transfer flow has a different UX goal than the Contact Details sidebar:
 * users want to quickly identify the best destination for the call, not see
 * every raw phone number associated with the contact (per CUU2-2864).
 *
 * 1. Directory contacts (source === 'Bhive'):
 *    - Surface a synthetic "Office" entry at the top using the contact's
 *      extension when available, otherwise the primary phone number.
 *    - Skip phones flagged `primary` (legacy parity — primary === office).
 *    - Dedup by normalized number against the Office entry.
 *    - Remaining distinct phones are surfaced with their type label.
 *
 * 2. External contacts (non-directory):
 *    - All non-duplicate phone numbers labelled by type.
 *    - Extensions appended with the "Extension" label, deduped against phones.
 */
export function mapContactToPhoneNumbers(contact: ContactModelV2): TransferPhoneNumber[] {
  const isDirectoryContact = contact.source === DIRECTORY_SOURCE;

  if (isDirectoryContact) {
    return mapDirectoryContact(contact);
  }

  return mapExternalContact(contact);
}

function mapDirectoryContact(contact: ContactModelV2): TransferPhoneNumber[] {
  const result: TransferPhoneNumber[] = [];
  const seenKeys = new Set<string>();

  const officeNumber = contact.extensionNumber || contact.phoneNumber;
  if (officeNumber) {
    result.push({ number: officeNumber, label: OFFICE_LABEL });
    const key = normalizePhoneNumber(officeNumber);
    if (key) {
      seenKeys.add(key);
    }
  }

  const remaining = deduplicatePhoneEntries(
    contact.nonDuplicatePhoneNumbers ?? [],
    seenKeys,
    (phone) => !phone.primary
  );

  result.push(
    ...remaining.map((phone) => ({
      number: phone.number,
      label: getPhoneLabel(phone.type),
    }))
  );

  return result;
}

function mapExternalContact(contact: ContactModelV2): TransferPhoneNumber[] {
  const seenKeys = new Set<string>();

  const phones = deduplicatePhoneEntries(
    contact.nonDuplicatePhoneNumbers ?? [],
    seenKeys
  ).map((phone) => ({
    number: phone.number,
    label: getPhoneLabel(phone.type),
  }));

  const extensions = deduplicatePhoneEntries(
    contact.extensionNumbers ?? [],
    seenKeys
  ).map((ext) => ({
    number: ext.number,
    label: EXTENSION_LABEL,
  }));

  return [...phones, ...extensions];
}

/**
 * Resolves the phone numbers and load state for a transfer target.
 * Pure function — no side effects, safe to call outside a hook.
 *
 * Resolution strategy:
 * - For direct-number targets (type === 'number'): returns the phoneNumbers as-is
 * - For directory contacts (sourceId present): reads reactively from personStore
 *   via `selectPersonValueById` to get fresh contact data
 * - For external contacts (contactId present): looks up in personStore.allContacts
 * - Falls back to the phoneNumbers already on the TransferTarget
 */
export function resolvePhoneNumbers(
  transferTarget: TransferTarget | null,
  personStore: PersonStore
): ResolvePhoneNumbersReturn {
  if (!transferTarget) {
    return { phoneNumbers: [], isLoaded: false };
  }

  if (transferTarget.type === 'number') {
    return { phoneNumbers: transferTarget.phoneNumbers, isLoaded: true };
  }

  if (transferTarget.sourceId) {
    const contact = personStore?.selectPersonValueById(Number(transferTarget.sourceId));
    const phoneNumbers = contact ? mapContactToPhoneNumbers(contact) : transferTarget.phoneNumbers;
    const isLoaded = !!contact || phoneNumbers.length > 0;
    return { phoneNumbers, isLoaded };
  }

  if (transferTarget.contactId) {
    const contact = personStore?.allContacts.find((c) => c.id === transferTarget.contactId);
    const phoneNumbers = contact ? mapContactToPhoneNumbers(contact) : transferTarget.phoneNumbers;
    const isLoaded = !!contact || phoneNumbers.length > 0;
    return { phoneNumbers, isLoaded };
  }

  return {
    phoneNumbers: transferTarget.phoneNumbers,
    isLoaded: transferTarget.phoneNumbers.length > 0,
  };
}
